import '@/assets/toast.scss'
import router from '@/router'
import { configureEcho } from '@laravel/echo-vue'
import { VueQueryPlugin } from '@tanstack/vue-query'
import { createHead } from '@unhead/vue'
import VueDatePicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import { MotionPlugin } from '@vueuse/motion'
import { isAxiosError } from 'axios'
import FloatingVue from 'floating-vue'
import 'floating-vue/dist/style.css'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import 'mapbox-gl/dist/mapbox-gl.css'
import { createApp } from 'vue'
import VueClipboard from 'vue3-clipboard'
import Popper from 'vue3-popper'
import { VueUiRadar } from 'vue-data-ui'
import 'vue-data-ui/style.css'
import { vue3Debounce } from 'vue-debounce'
import VueDragscroll from 'vue-dragscroll'
import 'vue-leaflet-markercluster/dist/style.css'
import VueSlider from 'vue-slider-component'
import 'vue-slider-component/theme/default.css'
import Toast, { PluginOptions } from 'vue-toastification'
import 'vue-toastification/dist/index.css'

import App from './App.vue'
import './assets/index.css'
import axios from './lib/axios'
import './lib/echo'
import pinia from './stores'

//Remove the console log in production
if (process.env.NODE_ENV === 'production') {
	console.log = () => {}
	console.error = () => {}
	console.debug = () => {}
	console.warn = () => {}
}

//Add polly for the mapbox and others libs
;(function () {
	const supportsPassive = (() => {
		let passive = false
		try {
			const opts = Object.defineProperty({}, 'passive', {
				get() {
					passive = true
					return true
				},
			})
			window.addEventListener('testPassive', null as any, opts)
			window.removeEventListener('testPassive', null as any, opts)
		} catch (e) {}
		return passive
	})()

	if (supportsPassive) {
		const originalAddEventListener = EventTarget.prototype.addEventListener
		EventTarget.prototype.addEventListener = function (
			type: string,
			listener: EventListenerOrEventListenerObject | null,
			options?: boolean | AddEventListenerOptions
		) {
			const passiveEvents = ['touchstart', 'touchmove', 'wheel', 'mousewheel']
			if (passiveEvents.includes(type) && options !== false) {
				const opts =
					typeof options === 'object' ?
						{ ...options, passive: options.passive ?? true }
					:	{ passive: true }
				return originalAddEventListener.call(this, type, listener, opts)
			}
			return originalAddEventListener.call(this, type, listener, options)
		}
	}
})()

globalThis.L = L

configureEcho({
	broadcaster: 'reverb',
	key: import.meta.env.VITE_REVERB_APP_KEY,
	wsHost: import.meta.env.VITE_REVERB_HOST,
	wsPort: import.meta.env.VITE_REVERB_PORT,
	wssPort: import.meta.env.VITE_REVERB_PORT,
	forceTLS: (import.meta.env.VITE_REVERB_SCHEME ?? 'https') === 'https',
	enabledTransports: ['ws', 'wss'],
	authEndpoint: `${import.meta.env.VITE_PUBLIC_BACKEND_URL}/broadcasting/auth`,
	authorizer: (channel: { name: string }) => {
		return {
			authorize: (socketId: string, callback: Function) => {
				axios
					.post('/broadcasting/auth', {
						socket_id: socketId,
						channel_name: channel.name,
					})
					.then((response) => {
						callback(null, response.data)
					})
					.catch((error) => {
						callback(error)
					})
			},
		}
	},
} as any)

const head = createHead()

const STATUS_CODES = [403, 404, 500]

const options: PluginOptions = {
	timeout: 8000,
	transition: 'Vue-Toastification__fade',
	closeOnClick: true,
	pauseOnHover: true,
	hideProgressBar: false,
	icon: true,
	filterBeforeCreate: (toast, toasts) => {
		if (toasts.filter((t) => t.type === toast.type).length !== 0) {
			return false
		}
		return toast
	},
}

const app = createApp(App)
	.directive('debounce', vue3Debounce({ lock: true }))
	.component('VueSlider', VueSlider)
	.component('VueDatePicker', VueDatePicker)
	.component('Popper', Popper)
	.component('VueUiRadar', VueUiRadar)
	.use(FloatingVue)
	.use(pinia)
	.use(router)
	.use(head)
	.use(MotionPlugin)
	.use(Toast, options)
	.use(VueDragscroll)
	.use(VueClipboard, {
		autoSetContainer: true,
		appendToBody: true,
	})

VueQueryPlugin.install(app, {
	queryClientConfig: {
		defaultOptions: {
			queries: {
				cacheTime: 1000 * 60,
				retry(failureCount, error: any) {
					if (
						isAxiosError(error) &&
						STATUS_CODES.includes(error.response?.status as number)
					) {
						return false
					}
					return failureCount < 3
				},
			},
		},
	},
})

router.isReady().then(() => {
	app.mount('#app')
})
